import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Search, Shield, UserPlus } from 'lucide-react'
import type { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { KidsEditForm } from './admin/KidsEditForm'
import { COULEUR_DU_NIVEAU, libelleDuNiveau } from '../../lib/inclusion/niveaux'

/*
  Gestion › Inclusion (26/10/2026, demande d'Éclosion) : les enfants dont le
  parent a déclaré des besoins particuliers, ou que l'équipe a placés à un
  niveau d'accès, TOUS CENTRES confondus. La coordination y trouve vite de quoi
  bien les accueillir, complète leur fiche, change leur niveau si leur
  situation évolue, et passe en inclusion un enfant qui en a besoin (fiche,
  « Journée en stage »). Accepter une demande reste à l'administration
  (Planification › Demandes › Inclusion).

  Aucun droit nouveau : l'équipe lit et modifie déjà toutes les fiches ; le
  niveau, lui, est gardé par la base (administration et coordination seules).
*/

type Niveau = 1 | 2 | 3

interface EnfantEnInclusion {
  kidId: string
  prenom: string
  nom: string
  dateNaissance: string | null
  parent: { prenom: string | null; nom: string | null; telephone: string | null } | null
  besoinsDeclares: boolean
  niveau: Niveau | null
  niveauLe: string | null
  situation: string | null
}

interface LigneLue {
  kid_id: string
  has_needs: boolean | null
  niveau_acces: number | null
  niveau_acces_le: string | null
  situation_details: string | null
  kid: Kid | Kid[] | null
}
interface Kid {
  prenom: string | null
  nom: string | null
  date_naissance: string | null
  parent: Parent | Parent[] | null
}
interface Parent { prenom: string | null; nom: string | null; telephone: string | null }
interface Trouve { id: string; prenom: string | null; nom: string | null; date_naissance: string | null }

type Filtre = 'tous' | 'a-examiner' | Niveau

const un = <T,>(v: T | T[] | null | undefined): T | null => (Array.isArray(v) ? v[0] ?? null : v ?? null)
const niveauLu = (n: number | null): Niveau | null => (n === 1 || n === 2 || n === 3 ? n : null)

const age = (dateNaissance: string | null) => {
  if (!dateNaissance) return null
  const n = new Date(dateNaissance)
  if (Number.isNaN(n.getTime())) return null
  const auj = new Date()
  let a = auj.getFullYear() - n.getFullYear()
  if (auj.getMonth() < n.getMonth() || (auj.getMonth() === n.getMonth() && auj.getDate() < n.getDate())) a--
  return a
}

/* Une saisie libre dans un filtre `or` de PostgREST : ni virgule ni parenthèse, ni joker. */
const pourLaRecherche = (texte: string) => texte.replace(/[,()%*\\]/g, ' ').trim()

export function InclusionManagement() {
  const db = supabase as unknown as SupabaseClient
  const [enfants, setEnfants] = useState<EnfantEnInclusion[]>([])
  const [chargement, setChargement] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)
  const [filtre, setFiltre] = useState<Filtre>('tous')
  const [recherche, setRecherche] = useState('')
  const [ouvert, setOuvert] = useState<string | null>(null)
  const [autre, setAutre] = useState('')
  const [trouves, setTrouves] = useState<Trouve[]>([])
  const [rechercheEnCours, setRechercheEnCours] = useState(false)

  const charger = useCallback(async () => {
    setChargement(true)
    setErreur(null)
    const { data, error } = await db
      .from('kid_inclusion')
      .select(`kid_id, has_needs, niveau_acces, niveau_acces_le, situation_details,
        kid:kids!inner(prenom, nom, date_naissance, parent:users!kids_user_id_fkey(prenom, nom, telephone))`)
      .or('has_needs.eq.true,niveau_acces.not.is.null')
    setChargement(false)
    if (error) {
      setErreur(error.message)
      return
    }
    setEnfants(((data ?? []) as LigneLue[]).map((l) => {
      const kid = un(l.kid)
      return {
        kidId: l.kid_id,
        prenom: kid?.prenom ?? '',
        nom: kid?.nom ?? '',
        dateNaissance: kid?.date_naissance ?? null,
        parent: un(kid?.parent),
        besoinsDeclares: l.has_needs === true,
        niveau: niveauLu(l.niveau_acces),
        niveauLe: l.niveau_acces_le,
        situation: l.situation_details,
      }
    }))
  }, [db])

  useEffect(() => {
    void charger()
  }, [charger])

  /* Chercher un enfant hors de la liste, pour le passer en inclusion. */
  useEffect(() => {
    const q = pourLaRecherche(autre)
    if (q.length < 2) {
      setTrouves([])
      return
    }
    const minuterie = setTimeout(async () => {
      setRechercheEnCours(true)
      const { data } = await db
        .from('kids')
        .select('id, prenom, nom, date_naissance')
        .or(`nom.ilike.%${q}%,prenom.ilike.%${q}%`)
        .order('nom')
        .limit(15)
      setTrouves((data ?? []) as Trouve[])
      setRechercheEnCours(false)
    }, 300)
    return () => clearTimeout(minuterie)
  }, [autre, db])

  const comptes = useMemo(() => ({
    tous: enfants.length,
    'a-examiner': enfants.filter((e) => e.niveau === null).length,
    1: enfants.filter((e) => e.niveau === 1).length,
    2: enfants.filter((e) => e.niveau === 2).length,
    3: enfants.filter((e) => e.niveau === 3).length,
  }), [enfants])

  const visibles = useMemo(() => {
    const q = recherche.trim().toLowerCase()
    return enfants
      .filter((e) => filtre === 'tous' || (filtre === 'a-examiner' ? e.niveau === null : e.niveau === filtre))
      .filter((e) => !q || [e.prenom, e.nom, e.parent?.prenom, e.parent?.nom].some((t) => t?.toLowerCase().includes(q)))
      /* À examiner d'abord : c'est là qu'il y a quelque chose à faire. */
      .sort((a, b) => Number(a.niveau !== null) - Number(b.niveau !== null)
        || `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`))
  }, [enfants, filtre, recherche])

  if (ouvert) {
    return (
      <KidsEditForm
        kidId={ouvert}
        ongletInitial="vie"
        onBack={() => {
          setOuvert(null)
          void charger()
        }}
      />
    )
  }

  const FILTRES: { cle: Filtre; libelle: string }[] = [
    { cle: 'tous', libelle: 'Tous' },
    { cle: 'a-examiner', libelle: 'À examiner' },
    { cle: 1, libelle: 'Niveau 1' },
    { cle: 2, libelle: 'Niveau 2' },
    { cle: 3, libelle: 'Niveau 3' },
  ]
  const dansLaListe = new Map(enfants.map((e) => [e.kidId, e]))

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
          <Shield className="h-6 w-6 text-blue-600" aria-hidden="true" /> Inclusion
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Les enfants en inclusion, tous centres : leur fiche pour bien les accueillir, et leur niveau d'accès.
          Accepter une demande reste à l'administration.
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            type="search"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Chercher un enfant ou un parent…"
            aria-label="Chercher dans la liste"
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer par niveau">
          {FILTRES.map((f) => (
            <button
              key={String(f.cle)}
              type="button"
              onClick={() => setFiltre(f.cle)}
              aria-pressed={filtre === f.cle}
              className={`rounded-full px-3 py-1 text-sm ${filtre === f.cle ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {f.libelle} ({comptes[f.cle]})
            </button>
          ))}
        </div>
      </div>

      {chargement ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : erreur ? (
        <p className="rounded-lg bg-red-50 p-4 text-sm text-red-700">{erreur}</p>
      ) : visibles.length === 0 ? (
        <p className="rounded-lg bg-white p-6 text-center text-sm text-gray-600 shadow-sm">Aucun enfant ne correspond.</p>
      ) : (
        <ul className="divide-y rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
          {visibles.map((e) => {
            const a = age(e.dateNaissance)
            return (
              <li key={e.kidId} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">
                    {e.prenom} {e.nom}{a !== null && <span className="font-normal text-gray-500"> · {a} ans</span>}
                    <span className={`ml-2 inline-block rounded-full px-2 py-0.5 align-middle text-xs font-normal ${COULEUR_DU_NIVEAU[e.niveau ?? 'aucun']}`}>
                      {libelleDuNiveau(e.niveau)}
                    </span>
                    {!e.besoinsDeclares && (
                      <span className="ml-2 inline-block rounded-full bg-gray-100 px-2 py-0.5 align-middle text-xs font-normal text-gray-700">
                        Besoins décochés
                      </span>
                    )}
                  </p>
                  {e.situation && <p className="truncate text-sm text-gray-600">{e.situation}</p>}
                  {e.parent && (
                    <p className="text-xs text-gray-500">
                      {e.parent.prenom} {e.parent.nom}
                      {e.parent.telephone && (
                        <> · <a href={`tel:${e.parent.telephone.replace(/\s/g, '')}`} className="underline">{e.parent.telephone}</a></>
                      )}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setOuvert(e.kidId)}
                  className="self-start rounded-lg border border-blue-200 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-50 sm:self-center"
                >
                  Ouvrir la fiche
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <section className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <UserPlus className="h-5 w-5 text-blue-600" aria-hidden="true" /> Passer un autre enfant en inclusion
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Un enfant qui a besoin d'une attention particulière : ouvrez sa fiche, « Journée en stage », cochez ses
          besoins spécifiques et décrivez-les, puis choisissez son niveau d'accès.
        </p>
        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            type="search"
            value={autre}
            onChange={(e) => setAutre(e.target.value)}
            placeholder="Nom ou prénom de l'enfant…"
            aria-label="Chercher un enfant"
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm"
          />
        </div>
        {rechercheEnCours && <p className="mt-2 text-xs text-gray-500">Recherche…</p>}
        {trouves.length > 0 && (
          <ul className="mt-3 divide-y rounded-lg border">
            {trouves.map((k) => {
              const deja = dansLaListe.get(k.id)
              return (
                <li key={k.id} className="flex items-center justify-between gap-3 p-3 text-sm">
                  <span>
                    {k.prenom} {k.nom}
                    {deja && (
                      <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${COULEUR_DU_NIVEAU[deja.niveau ?? 'aucun']}`}>
                        {libelleDuNiveau(deja.niveau)}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => setOuvert(k.id)}
                    className="rounded-lg border border-blue-200 px-3 py-1 text-sm font-medium text-blue-700 hover:bg-blue-50"
                  >
                    Ouvrir la fiche
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </div>
  )
}
